import { InferenceRule, InferredRelationshipData } from "../InferenceEngine";
import { KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import Diagram from "@semantical/modules/diagram/Diagram";
import { isSubObjectPropertyOf, SubObjectPropertyOf } from "@semantical/modules/owl2";
import { getPropertyIRI } from "@semantical/modules/owl2/types/PropertyExpression";
import { iriToString } from "@semantical/modules/owl2/types/IRI";
import type { IRI } from "@semantical/modules/owl2/types/IRI";
import type { ObjectPropertyChain, ObjectPropertyExpression } from "@semantical/modules/owl2/types/PropertyExpression";

/**
 * Helper to convert IRI to a full URI string.
 */
function iriToFullString(iri: IRI, prefixes?: Map<string, string>): string {
    if (iri.kind === 'FullIRI') {
        return iri.value;
    }
    // For abbreviated IRIs, try to expand using prefixes
    if (prefixes) {
        for (const [namespace, prefix] of prefixes) {
            if (prefix === iri.prefix) {
                return namespace + iri.localName;
            }
        }
    }
    return iriToString(iri);
}

/**
 * Check if a SubObjectPropertyExpression is an ObjectPropertyChain.
 */
function isPropertyChain(expr: SubObjectPropertyOf['subProperty']): expr is ObjectPropertyChain {
    return (expr as ObjectPropertyChain).type === 'ObjectPropertyChain';
}

/**
 * Extract the URI from an ObjectPropertyExpression.
 */
function getPropertyUri(expr: ObjectPropertyExpression, prefixes?: Map<string, string>): string {
    const iri = getPropertyIRI(expr);
    return iriToFullString(iri, prefixes);
}

/**
 * Represents a parsed property chain: chain properties -> result property
 */
interface PropertyChainDefinition {
    /** URIs of the chain properties in order */
    chainPropertyUris: string[];
    /** URI of the result property (super property) */
    resultPropertyUri: string;
}

/**
 * Property Chain inference rule.
 *
 * Implements OWL2 property chain axioms:
 * If a property P has a chain axiom P1 o P2 o ... o Pn => P,
 * then for any chain of relationships A-P1->B-P2->C-...-Pn->Z,
 * we infer A-P->Z.
 *
 * Example:
 * - Ontology declares: hasParent o hasParent => hasAncestor
 * - Knowledge graph has: Alice-hasParent->Bob-hasParent->Carol
 * - Rule infers: Alice-hasAncestor->Carol
 */
export class PropertyChainRule implements InferenceRule {
    id = "property-chain";
    name = "Property Chain Rule";
    description = "If P1 o P2 o ... o Pn => P and A-P1->B-P2->...-Pn->Z exists, infer A-P->Z";

    infer(knowledgeGraph: KnowledgeGraph, _diagram: Diagram): InferredRelationshipData[] {
        const inferred: InferredRelationshipData[] = [];

        // Get the ontology schema to access axioms
        const ontologySchema = knowledgeGraph.getOntologySchema();
        if (!ontologySchema) {
            console.debug("PropertyChainRule: No ontology schema available, skipping inference");
            return inferred;
        }
        if (!ontologySchema.ontology) {
            console.debug("PropertyChainRule: Ontology schema has no parsed ontology, skipping inference");
            return inferred;
        }

        const ontology = ontologySchema.ontology;
        const prefixes = ontologySchema.prefixes;

        // Find all property chain axioms
        const chainDefinitions: PropertyChainDefinition[] = [];

        for (const axiom of ontology.axioms) {
            if (isSubObjectPropertyOf(axiom)) {
                const subPropAxiom = axiom as SubObjectPropertyOf;

                // Check if the subProperty is a property chain
                if (isPropertyChain(subPropAxiom.subProperty)) {
                    const chain = subPropAxiom.subProperty;
                    const chainPropertyUris = chain.properties.map(p => getPropertyUri(p, prefixes));
                    const resultPropertyUri = getPropertyUri(subPropAxiom.superProperty, prefixes);

                    chainDefinitions.push({
                        chainPropertyUris,
                        resultPropertyUri
                    });

                    console.debug(
                        `PropertyChainRule: Found chain ${chainPropertyUris.join(' o ')} => ${resultPropertyUri}`
                    );
                }
            }
        }

        if (chainDefinitions.length === 0) {
            console.debug("PropertyChainRule: No property chain axioms found");
            return inferred;
        }

        // Build relationship index by subject URI for efficient lookup
        const relationships = Array.from(knowledgeGraph.relationships.values());
        const relationshipsBySubject = new Map<string, typeof relationships>();

        for (const rel of relationships) {
            const subjectUri = rel.subject.uri;
            if (!relationshipsBySubject.has(subjectUri)) {
                relationshipsBySubject.set(subjectUri, []);
            }
            relationshipsBySubject.get(subjectUri)!.push(rel);
        }

        // Build a set of existing relationship signatures for deduplication
        const existingSignatures = new Set<string>();
        for (const rel of relationships) {
            existingSignatures.add(`${rel.subject.uri}|${rel.predicate.uri}|${rel.object.uri}`);
        }

        // Track inferred signatures to avoid duplicates
        const inferredSignatures = new Set<string>();

        // For each chain definition, find matching chains in the knowledge graph
        for (const chainDef of chainDefinitions) {
            const chainResults = this.findChainMatches(
                chainDef.chainPropertyUris,
                relationshipsBySubject
            );

            for (const match of chainResults) {
                const inferredSubjectUri = match.startUri;
                const inferredPredicateUri = chainDef.resultPropertyUri;
                const inferredObjectUri = match.endUri;

                const signature = `${inferredSubjectUri}|${inferredPredicateUri}|${inferredObjectUri}`;

                // Check if this relationship already exists or has been inferred
                if (!existingSignatures.has(signature) && !inferredSignatures.has(signature)) {
                    inferred.push({
                        subjectUri: inferredSubjectUri,
                        predicateUri: inferredPredicateUri,
                        objectUri: inferredObjectUri,
                        ruleId: this.id
                    });
                    inferredSignatures.add(signature);

                    console.debug(
                        `PropertyChainRule: Inferred ${inferredSubjectUri} -${inferredPredicateUri}-> ${inferredObjectUri}`
                    );
                }
            }
        }

        return inferred;
    }

    /**
     * Find all matches for a property chain in the knowledge graph.
     * Returns pairs of (startUri, endUri) for each complete chain match.
     */
    private findChainMatches(
        chainPropertyUris: string[],
        relationshipsBySubject: Map<string, { subject: { uri: string }; predicate: { uri: string }; object: { uri: string } }[]>
    ): { startUri: string; endUri: string }[] {
        const results: { startUri: string; endUri: string }[] = [];

        if (chainPropertyUris.length === 0) {
            return results;
        }

        // Start with all relationships matching the first property in the chain
        const firstPropertyUri = chainPropertyUris[0];

        // Get all starting points (subjects of relationships with the first property)
        for (const [subjectUri, rels] of relationshipsBySubject) {
            const startingRels = rels.filter(r => r.predicate.uri === firstPropertyUri);

            for (const startRel of startingRels) {
                // Try to extend this chain
                const endPoints = this.extendChain(
                    startRel.object.uri,
                    chainPropertyUris.slice(1),
                    relationshipsBySubject
                );

                for (const endUri of endPoints) {
                    results.push({
                        startUri: subjectUri,
                        endUri
                    });
                }
            }
        }

        return results;
    }

    /**
     * Recursively extend a chain from a given node.
     * Returns all possible end URIs for the remaining chain.
     */
    private extendChain(
        currentUri: string,
        remainingPropertyUris: string[],
        relationshipsBySubject: Map<string, { subject: { uri: string }; predicate: { uri: string }; object: { uri: string } }[]>
    ): string[] {
        // Base case: no more properties in the chain
        if (remainingPropertyUris.length === 0) {
            return [currentUri];
        }

        const nextPropertyUri = remainingPropertyUris[0];
        const currentRels = relationshipsBySubject.get(currentUri) || [];
        const matchingRels = currentRels.filter(r => r.predicate.uri === nextPropertyUri);

        const results: string[] = [];

        for (const rel of matchingRels) {
            const endPoints = this.extendChain(
                rel.object.uri,
                remainingPropertyUris.slice(1),
                relationshipsBySubject
            );
            results.push(...endPoints);
        }

        return results;
    }
}

export default PropertyChainRule;
